import { z } from "zod"

interface FieldSchemaOptions {
  fieldName?: string
}

function baseNumberValidation(val: string): boolean {
  return !(!val || val.trim() !== val || val.startsWith("+") || /[eE]/.test(val))
}

function hasValidDecimalPoints(val: string): boolean {
  return !(val.startsWith(".") || val.endsWith("."))
}

const isNumeric = (val: string): boolean => {
  if (!baseNumberValidation(val)) return false
  if (!hasValidDecimalPoints(val)) return false

  const num = parseFloat(val)
  return !isNaN(num) && num >= 0 && isFinite(num)
}

const isInteger = (val: string): boolean => {
  if (!baseNumberValidation(val)) return false
  if (val.includes(".")) return false

  const num = parseInt(val, 10)
  return !isNaN(num) && num >= 0 && num.toString() === val
}

const isPositiveDecimal = (val: string): boolean => {
  if (!baseNumberValidation(val)) return false
  if (!hasValidDecimalPoints(val)) return false

  const num = parseFloat(val)
  return !isNaN(num) && num > 0 && isFinite(num)
}

const isPositiveInteger = (val: string): boolean => {
  if (!baseNumberValidation(val)) return false

  const num = parseInt(val, 10)
  return !isNaN(num) && num > 0 && num === parseFloat(val)
}

function createRequiredNumberSchema(
  validator: (val: string) => boolean,
  errorMessage: string,
  options?: FieldSchemaOptions
): z.ZodEffects<z.ZodString> {
  const requiredMessage = options?.fieldName ? `${options.fieldName} is required` : "This field is required"

  return z.string().min(1, requiredMessage).refine(validator, errorMessage)
}

function createOptionalNumberSchema(
  validator: (val: string) => boolean,
  errorMessage: string
): z.ZodEffects<z.ZodOptional<z.ZodString>> {
  return z
    .string()
    .optional()
    .refine((val) => !val || val === "" || validator(val), errorMessage)
}

export const numberSchemas = {
  requiredPositiveDecimalString: (options?: FieldSchemaOptions): z.ZodEffects<z.ZodString> =>
    createRequiredNumberSchema(isPositiveDecimal, "Must be a positive number", options),

  optionalPositiveDecimalString: (): z.ZodEffects<z.ZodOptional<z.ZodString>> =>
    createOptionalNumberSchema(isNumeric, "Must be a number"),

  optionalPositiveIntegerString: (): z.ZodEffects<z.ZodOptional<z.ZodString>> =>
    createOptionalNumberSchema(isInteger, "Must be a number"),

  requiredPositiveIntegerString: (options?: FieldSchemaOptions): z.ZodEffects<z.ZodString> =>
    createRequiredNumberSchema(isPositiveInteger, "Must be a positive integer", options),
} as const
